// Console authentication: OpenID Connect Authorization Code + PKCE against the auth server
// (public client `platform-console`). Tokens are kept in sessionStorage by oidc-client-ts.
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import type { RuntimeConfig } from "./config";

export const SCOPES = "openid profile email roles offline_access";
export const CALLBACK_PATH = "/auth/callback";

export function createUserManager(cfg: RuntimeConfig): UserManager {
  const origin = window.location.origin;
  return new UserManager({
    authority: cfg.authServerUrl,
    client_id: cfg.clientId,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    post_logout_redirect_uri: `${origin}/login?reason=signedOut`,
    response_type: "code",
    scope: SCOPES,
    automaticSilentRenew: true,
    // Refresh-token rotation (offline_access) — no hidden iframe needed.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    loadUserInfo: false,
  });
}

export class ConsoleAuth {
  private renewing: Promise<User | null> | null = null;

  constructor(readonly manager: UserManager) {}

  async user(): Promise<User | null> {
    return this.manager.getUser();
  }

  /** A valid access token, refreshing with the refresh token if expired. */
  async accessToken(): Promise<string | null> {
    const u = await this.manager.getUser();
    if (!u) return null;
    if (!u.expired) return u.access_token;
    if (!u.refresh_token) return null;
    this.renewing ??= this.manager
      .signinSilent()
      .catch(() => null)
      .finally(() => {
        this.renewing = null;
      });
    const renewed = await this.renewing;
    return renewed?.access_token ?? null;
  }

  signIn(returnTo = window.location.pathname + window.location.search): Promise<void> {
    return this.manager.signinRedirect({ state: { returnTo } });
  }

  /** Completes the redirect; returns the path to go back to. */
  async completeSignIn(): Promise<string> {
    const u = await this.manager.signinRedirectCallback();
    const state = u.state as { returnTo?: unknown } | undefined;
    const r =
      typeof state?.returnTo === "string" &&
      state.returnTo.startsWith("/") &&
      !state.returnTo.startsWith("//")
        ? state.returnTo
        : "/";
    return r === CALLBACK_PATH ? "/" : r;
  }

  /** Forget the stored tokens without contacting the auth server (expired session → back to /login). */
  async clear(): Promise<void> {
    await this.manager.removeUser();
  }

  async signOut(): Promise<void> {
    const u = await this.manager.getUser();
    await this.manager.removeUser();
    await this.manager.signoutRedirect(u?.id_token ? { id_token_hint: u.id_token } : {});
  }
}
